import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Collapse,
  IconButton,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  IconPrinter,
  IconDownload,
  IconChevronDown,
  IconChevronRight,
  IconCash,
  IconUsers,
  IconBus,
  IconPackage,
  IconTrendingUp,
  IconTrendingDown,
  IconFileText,
  IconReportAnalytics,
  IconPlus,
  IconPlayerPlay,
  IconEdit,
  IconTrash,
  IconChartBar,
  IconChartPie,
  IconRefresh,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import {
  fetchComprehensiveReport,
  fetchReportDefinitions,
  runReport,
  deleteReportDefinition,
} from '../slices/reportsSlice';
import axiosInstance from '../api/axiosInstance';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ReportBuilder from '../components/reports/ReportBuilder';
import ReportVisualization from '../components/reports/ReportVisualization';
import ScheduledReportsTab from '../components/reports/ScheduledReportsTab';
import ComparativeAnalysis from '../components/reports/ComparativeAnalysis';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);
const FONT = 'Cairo, Tajawal, sans-serif';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

const ReportsPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  
  // Redux state
  const {
    reportData,
    loading,
    error,
    reportDefinitions,
    currentReportResult,
  } = useSelector((state) => state.reports || {});
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Filter states
  const [period, setPeriod] = useState('monthly');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Report builder states
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  
  // Tree view states
  const [expandedNodes, setExpandedNodes] = useState({
    summary: true,
    financial: false,
    staff: false,
    buses: false,
    miscellaneous: false,
    store: false,
  });
  
  useEffect(() => {
    if (activeTab === 1) {
      dispatch(fetchReportDefinitions());
    }
  }, [activeTab, dispatch]);
  
  useEffect(() => {
    if (activeTab === 0) {
      handleFetchReport();
    }
  }, [period, activeTab]);
  
  const handleFetchReport = () => {
    const filters = { period };
    if (period === 'custom') {
      if (!dateFrom || !dateTo) {
        showToast('❌ يرجى تحديد تاريخ البداية والنهاية');
        return;
      }
      filters.date_from = dateFrom;
      filters.date_to = dateTo;
    }
    dispatch(fetchComprehensiveReport(filters));
  };
  
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };
  
  const handleExportExcel = () => {
    if (!reportData) {
      showToast('❌ لا توجد بيانات للتصدير');
      return;
    }
    
    const wb = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['التقرير الشامل'],
      ['الفترة', reportData.period],
      ['من تاريخ', reportData.date_from],
      ['إلى تاريخ', reportData.date_to],
      [],
      ['الإجمالي'],
      ['إجمالي الإيرادات', reportData.summary.total_revenue],
      ['إجمالي المصروفات', reportData.summary.total_expenses],
      ['إجمالي الأرباح', reportData.summary.total_benefits],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'الملخص');
    
    // Financial sheet
    if (reportData.financial) {
      const financialData = [
        ['التقارير المالية'],
        ['إجمالي المدفوعات', reportData.financial.payments.total],
        ['عدد المدفوعات', reportData.financial.payments.count],
        ['إجمالي المقبوضات', reportData.financial.recipients.total],
        ['عدد المقبوضات', reportData.financial.recipients.count],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(financialData);
      XLSX.utils.book_append_sheet(wb, ws2, 'المالية');
    }
    
    // Staff sheet
    if (reportData.staff) {
      const staffData = [
        ['التقارير - الموظفين'],
        ['إجمالي الرواتب', reportData.staff.total_salaries],
        ['إجمالي المدفوع', reportData.staff.total_paid],
        ['إجمالي المتبقي', reportData.staff.total_remaining],
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(staffData);
      XLSX.utils.book_append_sheet(wb, ws3, 'الموظفين');
    }
    
    XLSX.writeFile(wb, `تقرير_${period}_${new Date().toISOString().split('T')[0]}.xlsx`);
    showToast('✅ تم تصدير التقرير بنجاح');
  };
  
  const handleExportPDF = () => {
    if (!reportData) {
      showToast('❌ لا توجد بيانات للتصدير');
      return;
    }
    
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('التقرير الشامل', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(`الفترة: ${reportData.period}`, 20, 40);
    doc.text(`من: ${reportData.date_from}`, 20, 50);
    doc.text(`إلى: ${reportData.date_to}`, 20, 60);
    
    let yPos = 80;
    
    // Summary
    doc.setFontSize(14);
    doc.text('الملخص', 20, yPos);
    yPos += 10;
    doc.setFontSize(10);
    doc.text(`إجمالي الإيرادات: ${reportData.summary.total_revenue.toFixed(2)} ₪`, 20, yPos);
    yPos += 10;
    doc.text(`إجمالي المصروفات: ${reportData.summary.total_expenses.toFixed(2)} ₪`, 20, yPos);
    yPos += 10;
    doc.text(`إجمالي الأرباح: ${reportData.summary.total_benefits.toFixed(2)} ₪`, 20, yPos);
    yPos += 20;
    
    // Financial
    if (reportData.financial) {
      doc.setFontSize(14);
      doc.text('التقارير المالية', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      doc.text(`المدفوعات: ${reportData.financial.payments.total.toFixed(2)} ₪`, 20, yPos);
      yPos += 10;
      doc.text(`المقبوضات: ${reportData.financial.recipients.total.toFixed(2)} ₪`, 20, yPos);
      yPos += 20;
    }
    
    doc.save(`تقرير_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('✅ تم تصدير التقرير بنجاح');
  };
  
  const handlePrint = () => {
    if (!reportData) {
      showToast('❌ لا توجد بيانات للطباعة');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
        <head>
          <title>التقرير الشامل</title>
          <meta charset="UTF-8">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Alexandria:wght@400;500;600;700&display=swap');
            body {
              font-family: 'Alexandria', Arial, sans-serif;
              direction: rtl;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #4d4cac;
            }
            .summary {
              margin: 20px 0;
              padding: 15px;
              background-color: #f5f5f5;
              border-radius: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: right;
            }
            th {
              background-color: #4d4cac;
              color: white;
            }
          </style>
        </head>
        <body>
          <h1>التقرير الشامل</h1>
          <div class="summary">
            <p><strong>الفترة:</strong> ${reportData.period}</p>
            <p><strong>من:</strong> ${reportData.date_from}</p>
            <p><strong>إلى:</strong> ${reportData.date_to}</p>
          </div>
          <h2>الملخص</h2>
          <table>
            <tr>
              <th>البند</th>
              <th>المبلغ</th>
            </tr>
            <tr>
              <td>إجمالي الإيرادات</td>
              <td>${Number(reportData.summary.total_revenue).toFixed(2)} ₪</td>
            </tr>
            <tr>
              <td>إجمالي المصروفات</td>
              <td>${Number(reportData.summary.total_expenses).toFixed(2)} ₪</td>
            </tr>
            <tr>
              <td>إجمالي الأرباح</td>
              <td>${Number(reportData.summary.total_benefits).toFixed(2)} ₪</td>
            </tr>
          </table>
          ${reportData.financial ? `
            <h2>التقارير المالية</h2>
            <table>
              <tr>
                <th>البند</th>
                <th>المبلغ</th>
                <th>العدد</th>
              </tr>
              <tr>
                <td>المدفوعات</td>
                <td>${Number(reportData.financial.payments.total).toFixed(2)} ₪</td>
                <td>${reportData.financial.payments.count}</td>
              </tr>
              <tr>
                <td>المقبوضات</td>
                <td>${Number(reportData.financial.recipients.total).toFixed(2)} ₪</td>
                <td>${reportData.financial.recipients.count}</td>
              </tr>
            </table>
          ` : ''}
          ${reportData.staff ? `
            <h2>تقارير الموظفين</h2>
            <table>
              <tr>
                <th>البند</th>
                <th>المبلغ</th>
              </tr>
              <tr>
                <td>إجمالي الرواتب</td>
                <td>${Number(reportData.staff.total_salaries).toFixed(2)} ₪</td>
              </tr>
              <tr>
                <td>إجمالي المدفوع</td>
                <td>${Number(reportData.staff.total_paid).toFixed(2)} ₪</td>
              </tr>
              <tr>
                <td>إجمالي المتبقي</td>
                <td>${Number(reportData.staff.total_remaining).toFixed(2)} ₪</td>
              </tr>
            </table>
          ` : ''}
          ${reportData.buses ? `
            <h2>تقارير الباصات</h2>
            <table>
              <tr>
                <th>البند</th>
                <th>المبلغ</th>
              </tr>
              <tr>
                <td>إجمالي تكاليف الباصات</td>
                <td>${Number(reportData.buses.total_costs).toFixed(2)} ₪</td>
              </tr>
            </table>
          ` : ''}
          ${reportData.miscellaneous ? `
            <h2>تقارير المتفرقات</h2>
            <table>
              <tr>
                <th>البند</th>
                <th>المبلغ</th>
              </tr>
              <tr>
                <td>إجمالي المتفرقات</td>
                <td>${Number(reportData.miscellaneous.total_amount).toFixed(2)} ₪</td>
              </tr>
              <tr>
                <td>المدفوع</td>
                <td>${Number(reportData.miscellaneous.total_paid).toFixed(2)} ₪</td>
              </tr>
              <tr>
                <td>المتبقي</td>
                <td>${Number(reportData.miscellaneous.total_remaining).toFixed(2)} ₪</td>
              </tr>
            </table>
          ` : ''}
          ${reportData.store ? `
            <h2>تقارير المتجر</h2>
            <table>
              <tr>
                <th>البند</th>
                <th>المبلغ</th>
              </tr>
              <tr>
                <td>الإيرادات</td>
                <td>${Number(reportData.store.revenue).toFixed(2)} ₪</td>
              </tr>
              <tr>
                <td>التكاليف</td>
                <td>${Number(reportData.store.cost).toFixed(2)} ₪</td>
              </tr>
              <tr>
                <td>الأرباح</td>
                <td>${Number(reportData.store.benefits).toFixed(2)} ₪</td>
              </tr>
            </table>
          ` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
  
  // Prepare chart data
  const revenueExpensesData = reportData ? [
    {
      name: 'الإيرادات',
      value: Number(reportData.summary.total_revenue || 0),
    },
    {
      name: 'المصروفات',
      value: Number(reportData.summary.total_expenses || 0),
    },
  ] : [];
  
  const expensesBreakdownData = reportData?.summary?.expenses_breakdown ? [
    { name: 'المدفوعات', value: Number(reportData.summary.expenses_breakdown.payments || 0) },
    { name: 'رواتب الموظفين', value: Number(reportData.summary.expenses_breakdown.staff_salaries || 0) },
    { name: 'تكاليف الباصات', value: Number(reportData.summary.expenses_breakdown.bus_costs || 0) },
    { name: 'مشتريات المتجر', value: Number(reportData.summary.expenses_breakdown.store_purchases || 0) },
  ] : [];
  
  const revenueBreakdownData = reportData?.summary?.revenue_breakdown ? [
    { name: 'المقبوضات', value: Number(reportData.summary.revenue_breakdown.recipients || 0) },
    { name: 'مبيعات المتجر', value: Number(reportData.summary.revenue_breakdown.store_sales || 0) },
    { name: 'المتفرقات المدفوعة', value: Number(reportData.summary.revenue_breakdown.miscellaneous_paid || 0) },
  ] : [];
  
  const COLORS = ['#4d4cac', '#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0'];
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleRunReport = async (id) => {
    try {
      await dispatch(runReport(id)).unwrap();
      setSelectedReportId(id);
      showToast('تم تشغيل التقرير بنجاح', 'success');
    } catch (error) {
      showToast(error.message || 'حدث خطأ', 'error');
    }
  };
  
  const handleExportReport = async (id, format) => {
    try {
      const response = await axiosInstance.get(
        `/api/reports/definitions/${id}/export_${format}/`,
        {
          responseType: 'blob',
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showToast(`تم تصدير التقرير كـ ${format.toUpperCase()}`, 'success');
    } catch (error) {
      showToast(error.response?.data?.error || error.message || 'حدث خطأ', 'error');
    }
  };
  
  const handleDeleteReport = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التقرير؟')) {
      try {
        await dispatch(deleteReportDefinition(id)).unwrap();
        showToast('تم حذف التقرير بنجاح', 'success');
        dispatch(fetchReportDefinitions());
      } catch (error) {
        showToast(error.message || 'حدث خطأ', 'error');
      }
    }
  };
  
  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconReportAnalytics size={30} color="#fff" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: FONT }}
              >
                التقارير والإحصائيات
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: FONT,
                }}
              >
                عرض وإنشاء وتصدير التقارير المالية والإدارية الشاملة
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {/* Tabs Panel */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #E5E7EB',
              px: 1,
              '& .MuiTab-root': {
                fontFamily: FONT,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.9rem',
                color: '#6B7280',
                minHeight: 56,
              },
              '& .MuiTab-root.Mui-selected': { color: '#4338CA' },
              '& .MuiTabs-indicator': { backgroundColor: '#4338CA', height: 3, borderRadius: 3 },
            }}
          >
            <Tab label="التقارير الشاملة" />
            <Tab label="منشئ التقارير" />
            <Tab label="التقارير المجدولة" />
            <Tab label="التحليل المقارن" />
          </Tabs>
          
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ p: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      bgcolor: '#EEF2FF',
                      borderRadius: 2,
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconFileText size={24} color="#4338CA" />
                  </Box>
                  <Typography variant="h6" fontWeight={800} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>
                    التقارير الشاملة
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    startIcon={<IconRefresh size={18} />}
                    onClick={handleFetchReport}
                    sx={{
                      bgcolor: '#4338CA',
                      color: '#fff',
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: 'none',
                      px: 2.5,
                      fontFamily: FONT,
                      '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                    }}
                  >
                    تحديث
                  </Button>
                  {reportData && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<IconDownload size={18} />}
                        onClick={handleExportExcel}
                        sx={{
                          borderRadius: 2.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          px: 2.5,
                          fontFamily: FONT,
                          borderColor: '#16A34A',
                          color: '#16A34A',
                          '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                        }}
                      >
                        Excel
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<IconDownload size={18} />}
                        onClick={handleExportPDF}
                        sx={{
                          borderRadius: 2.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          px: 2.5,
                          fontFamily: FONT,
                          borderColor: '#DC2626',
                          color: '#DC2626',
                          '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                        }}
                      >
                        PDF
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<IconPrinter size={18} />}
                        onClick={handlePrint}
                        sx={{
                          borderRadius: 2.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          px: 2.5,
                          fontFamily: FONT,
                          borderColor: '#4338CA',
                          color: '#4338CA',
                          '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                        }}
                      >
                        طباعة
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
          
          {/* Filters */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2.5,
              bgcolor: '#F8F9FF',
              border: '1px solid #E5E7EB',
            }}
            flexWrap="wrap"
            useFlexGap
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: FONT }}>الفترة</InputLabel>
              <Select
                value={period}
                label="الفترة"
                onChange={(e) => {
                  setPeriod(e.target.value);
                  if (e.target.value !== 'custom') {
                    setDateFrom('');
                    setDateTo('');
                  }
                }}
              >
                <MenuItem value="daily">يومي</MenuItem>
                <MenuItem value="weekly">أسبوعي</MenuItem>
                <MenuItem value="monthly">شهري</MenuItem>
                <MenuItem value="yearly">سنوي</MenuItem>
                <MenuItem value="custom">مخصص</MenuItem>
              </Select>
            </FormControl>
            {period === 'custom' && (
              <>
                <TextField
                  size="small"
                  label="من تاريخ"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  size="small"
                  label="إلى تاريخ"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
              </>
            )}
          </Stack>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#4338CA' }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2.5, fontFamily: FONT }}>{error}</Alert>
          ) : !reportData ? (
            <Alert severity="info" sx={{ borderRadius: 2.5, fontFamily: FONT }}>اختر فترة لعرض التقرير</Alert>
          ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MotionCard
                  elevation={0}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ bgcolor: '#DCFCE7', borderRadius: 2, p: 0.8, display: 'flex' }}>
                        <IconTrendingUp size={22} color="#16A34A" />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: FONT, color: '#6B7280' }}>إجمالي الإيرادات</Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={800} mt={1.5} sx={{ fontFamily: FONT, color: '#16A34A' }}>
                      {Number(reportData.summary.total_revenue).toFixed(2)} ₪
                    </Typography>
                  </CardContent>
                </MotionCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MotionCard
                  elevation={0}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ bgcolor: '#FEE2E2', borderRadius: 2, p: 0.8, display: 'flex' }}>
                        <IconTrendingDown size={22} color="#DC2626" />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: FONT, color: '#6B7280' }}>إجمالي المصروفات</Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={800} mt={1.5} sx={{ fontFamily: FONT, color: '#DC2626' }}>
                      {Number(reportData.summary.total_expenses).toFixed(2)} ₪
                    </Typography>
                  </CardContent>
                </MotionCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MotionCard
                  elevation={0}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ bgcolor: '#FEF3C7', borderRadius: 2, p: 0.8, display: 'flex' }}>
                        <IconCash size={22} color="#D97706" />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: FONT, color: '#6B7280' }}>إجمالي الأرباح</Typography>
                    </Stack>
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      mt={1.5}
                      sx={{ fontFamily: FONT, color: Number(reportData.summary.total_benefits) >= 0 ? '#16A34A' : '#DC2626' }}
                    >
                      {Number(reportData.summary.total_benefits).toFixed(2)} ₪
                    </Typography>
                  </CardContent>
                </MotionCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MotionCard
                  elevation={0}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.25, ease: 'easeOut' }}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ bgcolor: '#EEF2FF', borderRadius: 2, p: 0.8, display: 'flex' }}>
                        <IconFileText size={22} color="#4338CA" />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: FONT, color: '#6B7280' }}>الفترة</Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={800} mt={1.5} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>
                      {reportData.period === 'daily' ? 'يومي' :
                       reportData.period === 'weekly' ? 'أسبوعي' :
                       reportData.period === 'monthly' ? 'شهري' :
                       reportData.period === 'yearly' ? 'سنوي' : 'مخصص'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                      {reportData.date_from} - {reportData.date_to}
                    </Typography>
                  </CardContent>
                </MotionCard>
              </Grid>
            </Grid>
            
            {/* Charts */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB', height: '100%' }}
                >
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={2}>
                    <IconChartPie size={20} color="#4338CA" />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>توزيع الإيرادات والمصروفات</Typography>
                  </Stack>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={revenueExpensesData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {revenueExpensesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${Number(value).toFixed(2)} ₪`} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB', height: '100%' }}
                >
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={2}>
                    <IconChartBar size={20} color="#DC2626" />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>تفصيل المصروفات</Typography>
                  </Stack>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={expensesBreakdownData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `₪${value.toLocaleString('en-US')}`} />
                      <Tooltip formatter={(value) => `${Number(value).toFixed(2)} ₪`} />
                      <Bar dataKey="value" fill="#4d4cac" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB', height: '100%' }}
                >
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={2}>
                    <IconChartBar size={20} color="#16A34A" />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>تفصيل الإيرادات</Typography>
                  </Stack>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueBreakdownData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `₪${value.toLocaleString('en-US')}`} />
                      <Tooltip formatter={(value) => `${Number(value).toFixed(2)} ₪`} />
                      <Bar dataKey="value" fill="#4caf50" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
            
            {/* Tree View Reports */}
            <Paper
              elevation={0}
              sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB' }}
            >
              <Typography variant="subtitle1" fontWeight={800} mb={2} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>التقارير التفصيلية</Typography>
              
              {/* Summary Node */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ cursor: 'pointer', p: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => toggleNode('summary')}
                >
                  <IconButton size="small">
                    {expandedNodes.summary ? <IconChevronDown /> : <IconChevronRight />}
                  </IconButton>
                  <IconFileText size={20} />
                  <Typography variant="h6" sx={{ ml: 1 }}>الملخص العام</Typography>
                </Stack>
                <Collapse in={expandedNodes.summary}>
                  <Box sx={{ pl: 4, pt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none', height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">الإيرادات</Typography>
                            <Typography variant="h6">
                              {Number(reportData.summary.total_revenue).toFixed(2)} ₪
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              المقبوضات: {Number(reportData.summary.revenue_breakdown.recipients).toFixed(2)} ₪
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              مبيعات المتجر: {Number(reportData.summary.revenue_breakdown.store_sales).toFixed(2)} ₪
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              المتفرقات: {Number(reportData.summary.revenue_breakdown.miscellaneous_paid).toFixed(2)} ₪
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none', height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">المصروفات</Typography>
                            <Typography variant="h6">
                              {Number(reportData.summary.total_expenses).toFixed(2)} ₪
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              المدفوعات: {Number(reportData.summary.expenses_breakdown.payments).toFixed(2)} ₪
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              رواتب الموظفين: {Number(reportData.summary.expenses_breakdown.staff_salaries).toFixed(2)} ₪
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              تكاليف الباصات: {Number(reportData.summary.expenses_breakdown.bus_costs).toFixed(2)} ₪
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              مشتريات المتجر: {Number(reportData.summary.expenses_breakdown.store_purchases).toFixed(2)} ₪
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Financial Node */}
              {reportData.financial && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: 'pointer', p: 1, borderRadius: 2, transition: 'background-color .2s', '&:hover': { bgcolor: '#F8F9FF' } }}
                    onClick={() => toggleNode('financial')}
                  >
                    <IconButton size="small">
                      {expandedNodes.financial ? <IconChevronDown /> : <IconChevronRight />}
                    </IconButton>
                    <IconCash size={20} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, fontFamily: FONT, color: '#1E1B4B' }}>التقارير المالية</Typography>
                    <Chip label={`${reportData.financial.payments.count + reportData.financial.recipients.count} عملية`} size="small" sx={{ ml: 'auto', bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 700, fontFamily: FONT }} />
                  </Stack>
                  <Collapse in={expandedNodes.financial}>
                    <Box sx={{ pl: 4, pt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold">المدفوعات (سند صرف)</Typography>
                              <Typography variant="h5" color="error.main">
                                {Number(reportData.financial.payments.total).toFixed(2)} ₪
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                عدد العمليات: {reportData.financial.payments.count}
                              </Typography>
                              {Object.keys(reportData.financial.payments.by_type || {}).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" mb={1}>حسب النوع:</Typography>
                                  {Object.entries(reportData.financial.payments.by_type).map(([type, data]) => (
                                    <Stack key={type} direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                      <Typography variant="body2">{type}</Typography>
                                      <Typography variant="body2" fontWeight="bold">
                                        {Number(data.amount).toFixed(2)} ₪ ({data.count})
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold">المقبوضات (سند قبض)</Typography>
                              <Typography variant="h5" color="success.main">
                                {Number(reportData.financial.recipients.total).toFixed(2)} ₪
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                عدد العمليات: {reportData.financial.recipients.count}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Staff Node */}
              {reportData.staff && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: 'pointer', p: 1, borderRadius: 2, transition: 'background-color .2s', '&:hover': { bgcolor: '#F8F9FF' } }}
                    onClick={() => toggleNode('staff')}
                  >
                    <IconButton size="small">
                      {expandedNodes.staff ? <IconChevronDown /> : <IconChevronRight />}
                    </IconButton>
                    <IconUsers size={20} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, fontFamily: FONT, color: '#1E1B4B' }}>تقارير الموظفين</Typography>
                    <Chip label={`${reportData.staff.by_employee?.length || 0} موظف`} size="small" sx={{ ml: 'auto', bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 700, fontFamily: FONT }} />
                  </Stack>
                  <Collapse in={expandedNodes.staff}>
                    <Box sx={{ pl: 4, pt: 2 }}>
                      <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">إجمالي الرواتب</Typography>
                              <Typography variant="h6">
                                {Number(reportData.staff.total_salaries).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">المدفوع</Typography>
                              <Typography variant="h6" color="success.main">
                                {Number(reportData.staff.total_paid).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">المتبقي</Typography>
                              <Typography variant="h6" color="error.main">
                                {Number(reportData.staff.total_remaining).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                          </Grid>
                          {reportData.staff.by_employee && reportData.staff.by_employee.length > 0 && (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>الموظف</TableCell>
                                    <TableCell align="right">الراتب</TableCell>
                                    <TableCell align="right">المدفوع</TableCell>
                                    <TableCell align="right">المتبقي</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {reportData.staff.by_employee.map((emp, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{emp.employee_name}</TableCell>
                                      <TableCell align="right">{Number(emp.total_salary).toFixed(2)} ₪</TableCell>
                                      <TableCell align="right">{Number(emp.total_paid).toFixed(2)} ₪</TableCell>
                                      <TableCell align="right">{Number(emp.remaining).toFixed(2)} ₪</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Collapse>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Buses Node */}
              {reportData.buses && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: 'pointer', p: 1, borderRadius: 2, transition: 'background-color .2s', '&:hover': { bgcolor: '#F8F9FF' } }}
                    onClick={() => toggleNode('buses')}
                  >
                    <IconButton size="small">
                      {expandedNodes.buses ? <IconChevronDown /> : <IconChevronRight />}
                    </IconButton>
                    <IconBus size={20} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, fontFamily: FONT, color: '#1E1B4B' }}>تقارير الباصات</Typography>
                    <Chip label={`${Number(reportData.buses.total_costs).toFixed(2)} ₪`} size="small" sx={{ ml: 'auto', bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 700, fontFamily: FONT }} />
                  </Stack>
                  <Collapse in={expandedNodes.buses}>
                    <Box sx={{ pl: 4, pt: 2 }}>
                      <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                            إجمالي تكاليف الباصات: {Number(reportData.buses.total_costs).toFixed(2)} ₪
                          </Typography>
                          {reportData.buses.by_bus && reportData.buses.by_bus.length > 0 && (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>الباص</TableCell>
                                    <TableCell align="right">التكلفة</TableCell>
                                    <TableCell align="right">عدد العمليات</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {reportData.buses.by_bus.map((bus, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{bus.bus_name}</TableCell>
                                      <TableCell align="right">{Number(bus.total_cost).toFixed(2)} ₪</TableCell>
                                      <TableCell align="right">{bus.count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Collapse>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Miscellaneous Node */}
              {reportData.miscellaneous && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: 'pointer', p: 1, borderRadius: 2, transition: 'background-color .2s', '&:hover': { bgcolor: '#F8F9FF' } }}
                    onClick={() => toggleNode('miscellaneous')}
                  >
                    <IconButton size="small">
                      {expandedNodes.miscellaneous ? <IconChevronDown /> : <IconChevronRight />}
                    </IconButton>
                    <IconPackage size={20} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, fontFamily: FONT, color: '#1E1B4B' }}>تقارير المتفرقات</Typography>
                    <Chip label={`${Number(reportData.miscellaneous.total_amount).toFixed(2)} ₪`} size="small" sx={{ ml: 'auto', bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 700, fontFamily: FONT }} />
                  </Stack>
                  <Collapse in={expandedNodes.miscellaneous}>
                    <Box sx={{ pl: 4, pt: 2 }}>
                      <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">إجمالي المتفرقات</Typography>
                              <Typography variant="h6">
                                {Number(reportData.miscellaneous.total_amount).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">المدفوع</Typography>
                              <Typography variant="h6" color="success.main">
                                {Number(reportData.miscellaneous.total_paid).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">المتبقي</Typography>
                              <Typography variant="h6" color="error.main">
                                {Number(reportData.miscellaneous.total_remaining).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                          </Grid>
                          {Object.keys(reportData.miscellaneous.by_type || {}).length > 0 && (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>النوع</TableCell>
                                    <TableCell align="right">العدد</TableCell>
                                    <TableCell align="right">المبلغ</TableCell>
                                    <TableCell align="right">المدفوع</TableCell>
                                    <TableCell align="right">المتبقي</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(reportData.miscellaneous.by_type).map(([type, data]) => (
                                    <TableRow key={type}>
                                      <TableCell>{type}</TableCell>
                                      <TableCell align="right">{data.count}</TableCell>
                                      <TableCell align="right">{Number(data.amount).toFixed(2)} ₪</TableCell>
                                      <TableCell align="right">{Number(data.paid).toFixed(2)} ₪</TableCell>
                                      <TableCell align="right">{Number(data.remaining).toFixed(2)} ₪</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Collapse>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Store Node */}
              {reportData.store && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: 'pointer', p: 1, borderRadius: 2, transition: 'background-color .2s', '&:hover': { bgcolor: '#F8F9FF' } }}
                    onClick={() => toggleNode('store')}
                  >
                    <IconButton size="small">
                      {expandedNodes.store ? <IconChevronDown /> : <IconChevronRight />}
                    </IconButton>
                    <IconPackage size={20} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, fontFamily: FONT, color: '#1E1B4B' }}>تقارير المتجر</Typography>
                    <Chip 
                      label={Number(reportData.store.benefits) >= 0 ? `ربح: ${Number(reportData.store.benefits).toFixed(2)} ₪` : `خسارة: ${Number(reportData.store.benefits).toFixed(2)} ₪`} 
                      size="small" 
                      color={Number(reportData.store.benefits) >= 0 ? 'success' : 'error'}
                      sx={{ ml: 'auto' }} 
                    />
                  </Stack>
                  <Collapse in={expandedNodes.store}>
                    <Box sx={{ pl: 4, pt: 2 }}>
                      <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">الإيرادات</Typography>
                              <Typography variant="h6" color="success.main">
                                {Number(reportData.store.revenue).toFixed(2)} ₪
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {reportData.store.purchases_count} عملية شراء
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">التكاليف</Typography>
                              <Typography variant="h6" color="error.main">
                                {Number(reportData.store.cost).toFixed(2)} ₪
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {reportData.store.sales_count} عملية بيع
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle2" color="text.secondary">الأرباح</Typography>
                              <Typography 
                                variant="h6" 
                                color={Number(reportData.store.benefits) >= 0 ? 'success.main' : 'error.main'}
                              >
                                {Number(reportData.store.benefits).toFixed(2)} ₪
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Paper>
          </>
        )}
            </Box>
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            {showBuilder ? (
              <ReportBuilder
                reportDefinition={editingReport}
                onSave={() => {
                  setShowBuilder(false);
                  setEditingReport(null);
                  dispatch(fetchReportDefinitions());
                }}
                onCancel={() => {
                  setShowBuilder(false);
                  setEditingReport(null);
                }}
              />
            ) : (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                  <Typography variant="h6" fontWeight={800} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>التقارير المخصصة</Typography>
                  <Button
                    variant="contained"
                    startIcon={<IconPlus size={18} />}
                    onClick={() => {
                      setEditingReport(null);
                      setShowBuilder(true);
                    }}
                    sx={{
                      bgcolor: '#4338CA',
                      color: '#fff',
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: 'none',
                      px: 3,
                      py: 1,
                      fontFamily: FONT,
                      '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                    }}
                  >
                    إنشاء تقرير جديد
                  </Button>
                </Stack>
                
                {reportDefinitions && reportDefinitions.length > 0 ? (
                  <Grid container spacing={2}>
                    {reportDefinitions.map((report) => (
                      <Grid item xs={12} md={6} key={report.id}>
                        <Card elevation={0} sx={{ borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', boxShadow: 'none', height: '100%' }}>
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="start" flexWrap="wrap" gap={1.5}>
                              <Box>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>{report.name}</Typography>
                                <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                                  {report.module_display} - {report.chart_type_display}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<IconPlayerPlay size={16} />}
                                  onClick={() => handleRunReport(report.id)}
                                  sx={{ bgcolor: '#4338CA', color: '#fff', borderRadius: 2, fontWeight: 700, textTransform: 'none', boxShadow: 'none', fontFamily: FONT, '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}
                                >
                                  تشغيل
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleExportReport(report.id, 'csv')}
                                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontFamily: FONT, borderColor: '#E5E7EB', color: '#6B7280', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF', color: '#4338CA' } }}
                                >
                                  CSV
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<IconDownload size={16} />}
                                  onClick={() => handleExportReport(report.id, 'excel')}
                                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontFamily: FONT, borderColor: '#16A34A', color: '#16A34A', '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' } }}
                                >
                                  Excel
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<IconEdit size={16} />}
                                  onClick={() => {
                                    setEditingReport(report);
                                    setShowBuilder(true);
                                  }}
                                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontFamily: FONT, borderColor: '#4338CA', color: '#4338CA', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' } }}
                                >
                                  تعديل
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<IconTrash size={16} />}
                                  onClick={() => handleDeleteReport(report.id)}
                                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontFamily: FONT, borderColor: '#DC2626', color: '#DC2626', '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' } }}
                                >
                                  حذف
                                </Button>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{ p: 5, textAlign: 'center', borderRadius: 3, bgcolor: '#F8F9FF', border: '1px dashed #E5E7EB' }}
                  >
                    <IconReportAnalytics size={40} color="#4338CA" style={{ opacity: 0.6 }} />
                    <Typography sx={{ fontFamily: FONT, color: '#6B7280', fontWeight: 700, mt: 1 }}>لا توجد تقارير مخصصة</Typography>
                  </Paper>
                )}
                
                {selectedReportId && currentReportResult && (
                  <Box sx={{ mt: 3 }}>
                    <ReportVisualization
                      data={currentReportResult.data}
                      chartType={currentReportResult.chart_type}
                      reportName={currentReportResult.report_name}
                    />
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            <ScheduledReportsTab />
          </TabPanel>
          
          <TabPanel value={activeTab} index={3}>
            <ComparativeAnalysis />
          </TabPanel>
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default ReportsPage;
